import { compareByOrder } from "../../../erd-editor-schema/dist/orderKey.js";
import { DictionaryKind } from "./actions.js";
//#region src/engine/modules/dictionary/dictionaryIndex.ts
var COLLECTION_KEY = {
	[DictionaryKind.word]: "wordEntities",
	[DictionaryKind.term]: "termEntities",
	[DictionaryKind.domain]: "domainEntities"
};
var IDS_KEY = {
	[DictionaryKind.word]: "wordIds",
	[DictionaryKind.term]: "termIds",
	[DictionaryKind.domain]: "domainIds"
};
var normalizeDictionaryName = (name) => name.trim().normalize("NFC");
var splitDictionaryList = (text) => text.split(",").map(normalizeDictionaryName).filter((name) => name !== "");
var generations = /* @__PURE__ */ new WeakMap();
function raiseDictionaryGeneration(state, kind) {
	const collection = state.collections[COLLECTION_KEY[kind]];
	generations.set(collection, (generations.get(collection) ?? 0) + 1);
}
function heldPerSheet(kind, build) {
	const held = /* @__PURE__ */ new WeakMap();
	return (state) => {
		const collection = state.collections[COLLECTION_KEY[kind]];
		const ids = state.doc[IDS_KEY[kind]];
		const generation = generations.get(collection) ?? 0;
		const entry = held.get(collection);
		if (entry && entry.generation === generation && entry.ids === ids && entry.length === ids.length) return entry.value;
		const value = build(state, collection, ids);
		held.set(collection, {
			generation,
			ids,
			length: ids.length,
			value
		});
		return value;
	};
}
function sortRows(_state, collection, ids) {
	const rows = [];
	for (const id of ids) {
		const row = collection[id];
		if (row) rows.push(row);
	}
	return rows.sort(compareByOrder);
}
var SHEET_ROWS = {
	[DictionaryKind.word]: heldPerSheet(DictionaryKind.word, sortRows),
	[DictionaryKind.term]: heldPerSheet(DictionaryKind.term, sortRows),
	[DictionaryKind.domain]: heldPerSheet(DictionaryKind.domain, sortRows)
};
var orderedDictionaryRows = (state, kind) => SHEET_ROWS[kind](state);
function dictionaryCache(kind, build) {
	return heldPerSheet(kind, (state) => build(orderedDictionaryRows(state, kind)));
}
var createNode = () => ({ next: /* @__PURE__ */ new Map() });
function marksAt(root, text) {
	let node = root;
	for (let index = 0; index < text.length; index++) {
		const code = text.charCodeAt(index);
		let next = node.next.get(code);
		if (!next) {
			next = createNode();
			node.next.set(code, next);
		}
		node = next;
	}
	node.marks ??= {
		words: [],
		synonymOf: [],
		forbiddenOf: []
	};
	return node.marks;
}
function pushOnce(list, value) {
	if (!list.includes(value)) list.push(value);
}
function groupByName(rows, each) {
	const byName = /* @__PURE__ */ new Map();
	for (const row of rows) {
		const name = normalizeDictionaryName(row.name);
		if (!name) continue;
		const named = byName.get(name);
		if (named) named.push(row);
		else byName.set(name, [row]);
		each?.(row, name);
	}
	return byName;
}
var buildNameIndex = (rows) => ({
	rows,
	byName: groupByName(rows)
});
function buildWordIndex(words) {
	const root = createNode();
	return {
		rows: words,
		byName: groupByName(words, (word, name) => {
			marksAt(root, name).words.push(word);
			for (const synonym of splitDictionaryList(word.synonyms)) if (synonym !== name) pushOnce(marksAt(root, synonym).synonymOf, word);
			for (const forbidden of splitDictionaryList(word.forbiddenWords)) if (forbidden !== name) pushOnce(marksAt(root, forbidden).forbiddenOf, word);
		}),
		root
	};
}
var getWordIndex = dictionaryCache(DictionaryKind.word, buildWordIndex);
var getTermNames = dictionaryCache(DictionaryKind.term, buildNameIndex);
var getDomainNames = dictionaryCache(DictionaryKind.domain, buildNameIndex);
var findTerm = (state, name) => getTermNames(state).byName.get(normalizeDictionaryName(name))?.[0];
var findDomain = (state, name) => getDomainNames(state).byName.get(normalizeDictionaryName(name))?.[0];
//#endregion
export { buildNameIndex, buildWordIndex, dictionaryCache, findDomain, findTerm, getDomainNames, getTermNames, getWordIndex, normalizeDictionaryName, orderedDictionaryRows, raiseDictionaryGeneration, splitDictionaryList };
